/**
 * Which balance a leave request is measured against: the year the leave falls
 * in, as /api/leave/affected-years/ reports it (one entry per year holding
 * working days, each with that year's balance). Same per-year rule as
 * LeaveRequest.clean() on the server, which stays the final check.
 *
 * No answer is null, never another year's figure and never 0, so a balance that
 * has not loaded or failed to load cannot read as "insufficient".
 */

/** Days left in one year's balance for a leave category, or null if not known. */
export function availableDays(balance, category) {
  const raw = category === 'personal_bereavement'
    ? balance?.personal_bereavement_available
    : balance?.available_days;
  const days = parseFloat(raw);
  return Number.isNaN(days) ? null : days;
}

/**
 * The affected-years entry for leave wholly inside one year, or null while that
 * is unknown. An entry for a year outside the dates is a leftover from dates
 * since changed. The year is the one holding the working days, which need not be
 * the start date's (Sunday 31 Dec to Tuesday 2 Jan is all in January's year).
 */
export function leaveYearEntry(affectedYears, startDate, endDate) {
  if (!startDate || !endDate || affectedYears?.length !== 1) return null;
  const entry = affectedYears[0];
  const inRange = entry.year >= parseInt(startDate, 10) && entry.year <= parseInt(endDate, 10);
  return inRange && entry.balance ? entry : null;
}

/** Why the balance refuses this request, in the person's words, or '' if it doesn't. */
export function leaveBalanceRefusal({ category, days, spansMultipleYears, affectedYears, startDate, endDate }) {
  if (category !== 'annual' && category !== 'personal_bereavement') return '';
  const what = category === 'annual' ? 'annual leave balance' : 'personal/bereavement balance';
  if (spansMultipleYears && affectedYears?.length > 0) {
    for (const y of affectedYears) {
      const available = availableDays(y.balance, category);
      if (available !== null && y.days > available) {
        return `Insufficient ${what} for year ${y.year}. Available: ${available} days, Required: ${y.days} days`;
      }
    }
    return '';
  }
  const entry = leaveYearEntry(affectedYears, startDate, endDate);
  const available = entry ? availableDays(entry.balance, category) : null;
  if (available !== null && days > available) return `Insufficient ${what}. Available: ${available} days`;
  return '';
}
